import { defineStore } from "pinia";
import { DASHBOARD_RANGE_LAST_30_DAYS } from "@price-rescue/api-contracts";

import type { MerchantDashboardDto, MerchantDashboardFilters } from "./models.js";
import type { MerchantDashboardService } from "./service.js";

export const useDashboardStore = defineStore("merchant-dashboard", {
  state: (): {
    service?: MerchantDashboardService;
    dashboard?: MerchantDashboardDto;
    filters: MerchantDashboardFilters;
    loading: boolean;
    requestSequence: number;
    errorMessage?: string;
  } => ({
    filters: defaultDashboardFilters(),
    loading: false,
    requestSequence: 0,
  }),
  getters: {
    hasDashboard: (state): boolean => state.dashboard !== undefined,
  },
  actions: {
    configure(service: MerchantDashboardService): void {
      this.service = service;
    },
    async load(filters: MerchantDashboardFilters): Promise<void> {
      const requestSequence = ++this.requestSequence;
      this.filters = filters;
      delete this.dashboard;
      this.loading = true;
      delete this.errorMessage;
      try {
        const dashboard = await this.requireService().loadDashboard(filters);
        if (requestSequence === this.requestSequence) this.dashboard = dashboard;
      } catch (error: unknown) {
        if (requestSequence === this.requestSequence) this.errorMessage = messageForError(error);
      } finally {
        if (requestSequence === this.requestSequence) this.loading = false;
      }
    },
    requireService(): MerchantDashboardService {
      if (this.service === undefined) {
        throw new Error("Dashboard store was not configured before use.");
      }
      return this.service;
    },
  },
});

export function defaultDashboardFilters(): MerchantDashboardFilters {
  const toDay = new Date();
  const fromDay = new Date(toDay);
  fromDay.setUTCDate(fromDay.getUTCDate() - 29);
  return {
    range: DASHBOARD_RANGE_LAST_30_DAYS,
    fromDay: toIsoDay(fromDay),
    toDay: toIsoDay(toDay),
  };
}

function messageForError(error: unknown): string {
  if (error instanceof Error) return error.message;
  return "Dashboard data could not be loaded.";
}

function toIsoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}
